// admin-ui/src/features/c/components/LocationEditorForm.jsx


import React, { useEffect, useMemo, useState, useCallback } from 'react'
import PhotosPanel from './PhotosPanel'
import PhotosAddMore from './PhotosAddMore'
import WindProfilePanel from './WindProfilePanel'
import { createPortal } from 'react-dom'
import PolicyField from './PolicyField';


// ✅ richtiger Client-Pfad (Case-sensitiv, Vercel/Linux!)
import { supabase } from '../../../lib/supabaseClient'

import {
  getFieldPolicy,
  getPolicyHint,
} from '../policies/fieldPolicies'

export default function LocationEditorForm({ value, onSave, categoryNameById, categories }) {
  // ----------------------------
  // Base Form State
  // ----------------------------
  const [form, setForm] = useState({ ...value })
  const [proposalChanges, setProposalChanges] = useState({});
  const [extraProposalItems, setExtraProposalItems] = useState([])

  useEffect(() => {
    setForm({ ...value })
    setProposalChanges({})
    setExtraProposalItems([])
  }, [value?.id])

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }))

  const setProposal = (field, value) => {
    setProposalChanges((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const label = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }
  const input = { padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 8 }

  const boxStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 12,
    background: '#fff',
  }

  const catLabel = (id) => {
    if (id == null) return '—'
    return (categoryNameById && categoryNameById[id]) ?? String(id)
  }

  const sortedCategories = useMemo(() => {
    return (categories || []).slice().sort((a, b) => {
      const an = (a.name_de || a.name_en || `#${a.id}`).toLocaleLowerCase()
      const bn = (b.name_de || b.name_en || `#${b.id}`).toLocaleLowerCase()
      return an.localeCompare(bn)
    })
  }, [categories])

  // ----------------------------
  // Proposal: Felder / Diff
  // ----------------------------
  const PROPOSAL_FIELDS = useMemo(
    () => [
      'display_name',
      'active',
      'category_id',
      'lat',
      'lng',
      'address',
      'website',
      'phone',
      'name_de',
      'name_en',
      'name_it',
      'name_fr',
      'name_hr',
      'description_de',
      'description_en',
      'description_it',
      'description_fr',
      'description_hr',
    ],
    []
  )

  const normalize = (v) => (v === undefined ? null : v)

  const valuesEqual = (a, b) => {
    const va = normalize(a)
    const vb = normalize(b)
    if (typeof va === 'number' && typeof vb === 'number') return Object.is(va, vb)
    return JSON.stringify(va) === JSON.stringify(vb)
  }

  const inferValueType = (v) => {
    const x = normalize(v)
    if (x === null) return 'null'
    if (typeof x === 'boolean') return 'boolean'
    if (typeof x === 'number') return 'number'
    return 'text'
  }

  const buildChangedItems = useCallback(() => {
    const items = []
    for (const k of PROPOSAL_FIELDS) {
      const oldVal = value?.[k]
      const newVal = form?.[k]
      if (!valuesEqual(oldVal, newVal)) {
        items.push({
          field_path: k,
          old_value: normalize(oldVal),
          new_value: normalize(newVal),
          value_type: inferValueType(newVal),
        })
      }
    }
    return items
  }, [form, value, PROPOSAL_FIELDS])

const directChangedItems = useMemo(() => buildChangedItems(), [buildChangedItems])

const proposalChangedItems = useMemo(() => {
  return Object.entries(proposalChanges)
    .filter(([field, newVal]) => !valuesEqual(value?.[field], newVal))
    .map(([field, newVal]) => ({
      field_path: field,
      old_value: normalize(value?.[field]),
      new_value: normalize(newVal),
      value_type: inferValueType(newVal),
    }))
}, [proposalChanges, value])

const changedItems = useMemo(() => {
  const byField = new Map()

  for (const item of directChangedItems) {
    byField.set(`locations:${value.id}:${item.field_path}`, {
      table_name: 'locations',
      row_id: String(value.id),
      ...item,
    })
  }

  for (const item of proposalChangedItems) {
    byField.set(`locations:${value.id}:${item.field_path}`, {
      table_name: 'locations',
      row_id: String(value.id),
      ...item,
    })
  }

  for (const item of extraProposalItems) {
    byField.set(`${item.table_name}:${item.row_id}:${item.field_path}`, item)
  }

  return Array.from(byField.values())
}, [directChangedItems, proposalChangedItems, extraProposalItems, value.id])


const hasChanges = changedItems.length > 0

const UI_LANG = 'de' // später dynamisch: de | en | it | fr | hr

const TXT = {
  de: {
    submitProposal: 'Vorschlag einreichen',
    applyProposal: 'Vorschlag übernehmen',
    working: 'Arbeite…',
    noChanges: 'Keine Änderungen erkannt',
    submitTitle: 'Änderungen als Vorschlag einreichen',
    applyTitle: 'Vorschlag anwenden',
    summaryTitle: 'Änderungsvorschlag',
    old: 'Alt',
    next: 'Neu',
    changesOpen: 'Änderung(en) offen',
    changesDetected: 'Änderungen erkannt',
    noOpenChanges: 'keine offenen Änderungen',
    noProposal: 'Kein Vorschlag',
    proposalDraft: 'Vorschlag: Entwurf',
    proposalSubmitted: 'Vorschlag: Eingereicht',
    proposalApplied: 'Vorschlag: Übernommen',
    flowHint:
      'Änderungen werden als Vorschlag gespeichert. Ein Admin oder Editor kann sie anschließend prüfen und übernehmen.',
    translationHint:
      'Hinweis: Die Übersetzung füllt Felder nur im Formular. Zum Persistieren bitte danach den Vorschlag einreichen.',
  },
  en: {
    submitProposal: 'Submit proposal',
    applyProposal: 'Apply proposal',
    working: 'Working…',
    noChanges: 'No changes detected',
    submitTitle: 'Submit changes as proposal',
    applyTitle: 'Apply proposal',
    summaryTitle: 'Change proposal',
    old: 'Old',
    next: 'New',
    changesOpen: 'change(s) open',
    changesDetected: 'Changes detected',
    noOpenChanges: 'no open changes',
    noProposal: 'No proposal',
    proposalDraft: 'Proposal: Draft',
    proposalSubmitted: 'Proposal: Submitted',
    proposalApplied: 'Proposal: Applied',
    flowHint:
      'Changes are saved as proposal items. An admin or editor can review and apply them afterwards.',
    translationHint:
      'Note: Translation only fills the form fields. To persist them, please submit the proposal afterwards.',
  },
  it: {
    submitProposal: 'Invia proposta',
    applyProposal: 'Applica proposta',
    working: 'Elaborazione…',
    noChanges: 'Nessuna modifica rilevata',
    submitTitle: 'Invia le modifiche come proposta',
    applyTitle: 'Applica proposta',
    summaryTitle: 'Proposta di modifica',
    old: 'Vecchio',
    next: 'Nuovo',
    changesOpen: 'modifica/modifiche aperte',
    changesDetected: 'Modifiche rilevate',
    noOpenChanges: 'nessuna modifica aperta',
    noProposal: 'Nessuna proposta',
    proposalDraft: 'Proposta: Bozza',
    proposalSubmitted: 'Proposta: Inviata',
    proposalApplied: 'Proposta: Applicata',
    flowHint:
      'Le modifiche vengono salvate come proposta. Un admin o editor può poi verificarle e applicarle.',
    translationHint:
      'Nota: la traduzione compila solo i campi del modulo. Per salvarli, inviare poi la proposta.',
  },
  fr: {
    submitProposal: 'Soumettre la proposition',
    applyProposal: 'Appliquer la proposition',
    working: 'Traitement…',
    noChanges: 'Aucune modification détectée',
    submitTitle: 'Soumettre les modifications comme proposition',
    applyTitle: 'Appliquer la proposition',
    summaryTitle: 'Proposition de modification',
    old: 'Ancien',
    next: 'Nouveau',
    changesOpen: 'modification(s) ouverte(s)',
    changesDetected: 'Modifications détectées',
    noOpenChanges: 'aucune modification ouverte',
    noProposal: 'Aucune proposition',
    proposalDraft: 'Proposition : brouillon',
    proposalSubmitted: 'Proposition : soumise',
    proposalApplied: 'Proposition : appliquée',
    flowHint:
      'Les modifications sont enregistrées comme proposition. Un admin ou éditeur peut ensuite les vérifier et les appliquer.',
    translationHint:
      'Remarque : la traduction remplit uniquement les champs du formulaire. Pour les enregistrer, soumettez ensuite la proposition.',
  },
  hr: {
    submitProposal: 'Pošalji prijedlog',
    applyProposal: 'Primijeni prijedlog',
    working: 'Obrada…',
    noChanges: 'Nema otkrivenih izmjena',
    submitTitle: 'Pošalji izmjene kao prijedlog',
    applyTitle: 'Primijeni prijedlog',
    summaryTitle: 'Prijedlog izmjene',
    old: 'Staro',
    next: 'Novo',
    changesOpen: 'otvorenih izmjena',
    changesDetected: 'Izmjene otkrivene',
    noOpenChanges: 'nema otvorenih izmjena',
    noProposal: 'Nema prijedloga',
    proposalDraft: 'Prijedlog: nacrt',
    proposalSubmitted: 'Prijedlog: poslan',
    proposalApplied: 'Prijedlog: primijenjen',
    flowHint:
      'Izmjene se spremaju kao prijedlog. Admin ili editor ih zatim može provjeriti i primijeniti.',
    translationHint:
      'Napomena: prijevod popunjava samo polja u obrascu. Za spremanje pošaljite prijedlog.',
  },
}

const t = TXT[UI_LANG] || TXT.de

const PROPOSAL_FIELD_LABELS = {
  display_name: 'Anzeigename',
  active: 'Aktiv',
  category_id: 'Kategorie',
  lat: 'Latitude',
  lng: 'Longitude',
  address: 'Adresse',
  website: 'Website',
  phone: 'Telefon',
  name_de: 'Name DE',
  name_en: 'Name EN',
  name_it: 'Name IT',
  name_fr: 'Name FR',
  name_hr: 'Name HR',
  description_de: 'Beschreibung DE',
  description_en: 'Beschreibung EN',
  description_it: 'Beschreibung IT',
  description_fr: 'Beschreibung FR',
  description_hr: 'Beschreibung HR',
  value_json: 'Wind & Schwell',
}

const shortValue = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  const s = typeof v === 'string' ? v : JSON.stringify(v)
  return s.length > 90 ? `${s.slice(0, 90)}…` : s
}

const proposalSummaryItems = useMemo(() => {
  return changedItems.map((it) => ({
    ...it,
    label: PROPOSAL_FIELD_LABELS[it.field_path] || it.field_path,
    oldPreview: shortValue(it.old_value),
    newPreview: shortValue(it.new_value),
  }))
}, [changedItems])

  // ----------------------------
  // Proposal UX State
  // ----------------------------
  const [authUserId, setAuthUserId] = useState(null)
  const [userRole, setUserRole] = useState(null)

  const [proposalId, setProposalId] = useState(null)
  const [proposalStatus, setProposalStatus] = useState(null) // draft | submitted | applied | ...
  const [proposalLoading, setProposalLoading] = useState(false)
  const [proposalError, setProposalError] = useState(null)
  const [proposalInfo, setProposalInfo] = useState(null)

  const isPrivileged = userRole === 'admin' || userRole === 'editor'
  const canApplyNow = isPrivileged && proposalId && proposalStatus === 'submitted'

// ----------------------------
// Field Policies
// ----------------------------
const currentPolicyRole =
  userRole === 'admin'
    ? 'admin'
    : authUserId
      ? 'owner'
      : 'guest'

const websitePolicy = getFieldPolicy({
  scope: 'location',
  key: 'website',
  role: currentPolicyRole,
})

const phonePolicy = getFieldPolicy({
  scope: 'location',
  key: 'phone',
  role: currentPolicyRole,
})

const addressPolicy = getFieldPolicy({
  scope: 'location',
  key: 'address',
  role: currentPolicyRole,
})


const descriptionDePolicy = getFieldPolicy({
  scope: 'location',
  key: 'description_de',
  role: currentPolicyRole,
})

const openAuthModalForProposal = () => {
  setAuthError(null)
  setAuthStep('email')
  persistDraft()
  setAuthModalOpen(true)
}

const buildPolicyFieldProps = (field, policy) => {
  const locked = !policy.canEditDirectly

  return {
    value: form[field],
    onChange: (nextValue) => set(field, nextValue),
    locked,
    reason: getPolicyHint(policy),
    proposalMode: !!authUserId && locked,
    proposalValue: proposalChanges[field] ?? form[field] ?? '',
    onProposalChange: (nextValue) => setProposal(field, nextValue),
    onLoginRequired: openAuthModalForProposal,
  }
}

  // ----------------------------
  // Auth Modal State
  // ----------------------------
  const [authEmail, setAuthEmail] = useState('')
  const [authStep, setAuthStep] = useState('email') // 'email' | 'sent'
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState(null)

  // ----------------------------
  // Persist/Restore Form across Magic Link Redirect
  // ----------------------------
  const draftKey = useMemo(() => {
    const locId = value?.id ?? 'unknown'
    return `w2h:modc:draft:${locId}`
  }, [value?.id])

  const persistDraft = useCallback(() => {
    try {
      const payload = { t: Date.now(), locationId: value?.id ?? null, form }
      localStorage.setItem(draftKey, JSON.stringify(payload))
      localStorage.setItem('w2h:auth:returnTo', window.location.pathname + window.location.search)
    } catch (e) {
      console.warn('persistDraft failed', e)
    }
  }, [draftKey, form, value?.id])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (!parsed?.form) return
      if (String(parsed.locationId ?? '') !== String(value?.id ?? '')) return
      setForm((cur) => ({ ...cur, ...parsed.form }))
    } catch (e) {
      console.warn('restoreDraft failed', e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id])

  // ----------------------------
  // Auth boot + onAuthStateChange (nur 1 Quelle für authUserId)
  // ----------------------------
  useEffect(() => {
    let mounted = true

    supabase.auth.getSession().then(({ data }) => {
      const uid = data?.session?.user?.id ?? null
      if (mounted) setAuthUserId(uid)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null
      setAuthUserId(uid)

      if (uid) {
        setAuthModalOpen(false)
        setAuthError(null)
        setAuthStep('email')
      }
    })

    return () => {
      mounted = false
      sub?.subscription?.unsubscribe?.()
    }
  }, [])

  // Optional: falls Callback-Seite in anderem Tab läuft und ein Storage-Flag setzt
  useEffect(() => {
    function onStorage(e) {
      if (e.key === 'w2h:auth:done' && e.newValue) {
        supabase.auth.getSession().then(({ data }) => {
          const uid = data?.session?.user?.id ?? null
          setAuthUserId(uid)
        })
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // ----------------------------
  // Role + Latest Proposal nachziehen, wenn Auth + Location da sind
  // ----------------------------
  useEffect(() => {
    let alive = true

    async function run() {
      if (!authUserId || !value?.id) {
        setUserRole(null)
        setProposalId(null)
        setProposalStatus(null)
        return
      }

      try {
        const { data: prof, error: perr } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', authUserId)
          .maybeSingle()

        if (!alive) return

        if (perr) {
          console.warn('profiles role fetch error', perr)
          setUserRole(null)
        } else {
          setUserRole(prof?.role ?? null)
        }

        await loadLatestProposal(authUserId, value.id)
      } catch (e) {
        console.warn('role/proposal boot error', e)
      }
    }

    run()
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserId, value?.id])

  async function ensureAuthOrOpenModal() {
    const { data } = await supabase.auth.getUser()
    const uid = data?.user?.id ?? null
    if (!uid) {
      persistDraft()
      setAuthModalOpen(true)
      throw new Error('Nicht angemeldet')
    }
    return uid
  }

  async function sendLoginLink() {
    setAuthError(null)
    const email = (authEmail || '').trim()
    if (!email) {
      setAuthError('Bitte Email eingeben.')
      return
    }

    try {
      setAuthLoading(true)

      const next = window.location.pathname + window.location.search
      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`

console.log('MagicLink redirectTo:', redirectTo)

      persistDraft()

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      })
      if (error) throw error

      setAuthStep('sent')
    } catch (e) {
      setAuthError(e?.message || 'Login-Link konnte nicht gesendet werden.')
    } finally {
      setAuthLoading(false)
    }
  }

  // ✅ Logout für Testzwecke (deine Admin-Rolle bleibt DB-seitig, aber Session ist weg)
  async function signOut() {
    try {
      setProposalInfo(null)
      setProposalError(null)
      await supabase.auth.signOut()
      setAuthUserId(null)
      setUserRole(null)
      setProposalId(null)
      setProposalStatus(null)
      setProposalInfo('Ausgeloggt.')
    } catch (e) {
      setProposalError(e?.message || 'Logout fehlgeschlagen.')
    }
  }

  async function loadLatestProposal(uid, locId) {
    try {
      setProposalLoading(true)
      setProposalError(null)

      const { data, error } = await supabase
        .from('proposals')
        .select('id,status,created_at')
        .eq('created_by', uid)
        .eq('location_id', locId)
        .in('status', ['draft', 'submitted', 'applied'])
        .order('created_at', { ascending: false })
        .limit(1)

      if (error) throw error

      const p = (data && data[0]) || null
      setProposalId(p?.id ?? null)
      setProposalStatus(p?.status ?? null)
    } catch (e) {
      console.warn('loadLatestProposal error', e)
      setProposalId(null)
      setProposalStatus(null)
    } finally {
      setProposalLoading(false)
    }
  }

  async function reloadLocationFromDB() {
    if (!value?.id) return
    const { data, error } = await supabase.from('locations').select('*').eq('id', value.id).single()
    if (error) throw error
    setForm({ ...data })
  }

  // 1) Draft Proposal sicherstellen (oder reuse)
async function ensureDraftProposal(uid) {
  if (!uid) throw new Error('Nicht angemeldet.')

  if (proposalId && proposalStatus === 'draft') return proposalId

  const { data, error } = await supabase
    .from('proposals')
    .insert({
      proposal_type: 'update_location',
      status: 'draft',
      target_table: 'locations',
      target_id: String(value.id),
      location_id: value.id,
      created_by: uid,
      title: `Änderungsvorschlag #${value.id}`,
      message: 'Änderungsvorschlag aus Admin-UI (Modul C)',
    })
    .select('id,status')
    .single()

  if (error) throw error

  setProposalId(data.id)
  setProposalStatus(data.status)
  return data.id
}

  // 2) Proposal Items schreiben (ersetzen)
  async function upsertProposalItems(pId) {
    const { error: delErr } = await supabase.from('proposal_items').delete().eq('proposal_id', pId)
    if (delErr) throw delErr

    if (!hasChanges) return


const rows = changedItems.map((it) => ({
  proposal_id: pId,
  op: 'set',
  table_name: it.table_name || 'locations',
  row_id: String(it.row_id || value.id),
  field_path: it.field_path,
  old_value: it.old_value,
  new_value: it.new_value,
  value_type: it.value_type,
}))

    const { error: insErr } = await supabase.from('proposal_items').insert(rows)
    if (insErr) throw insErr
  }

  // 3) Proposal submit
  async function submitProposal() {
    if (proposalLoading) return

    setProposalError(null)
    setProposalInfo(null)
    setProposalLoading(true)

    try {
      const uid = await ensureAuthOrOpenModal()

      if (!hasChanges) {
        setProposalInfo('Keine Änderungen erkannt – nichts zum Einreichen.')
        return
      }

      const pId = await ensureDraftProposal(uid)
      await upsertProposalItems(pId)

      const { error } = await supabase
        .from('proposals')
        .update({ status: 'submitted', submitted_at: new Date().toISOString() })
        .eq('id', pId)

      if (error) throw error

      setProposalStatus('submitted')
      setProposalChanges({})
      setExtraProposalItems([])

      try {
        localStorage.removeItem(draftKey)
      } catch (_) {}

      setProposalInfo(`Proposal eingereicht (${changedItems.length} Änderung(en)).`)
    } catch (e) {
      setProposalError(e?.message || 'Fehler beim Einreichen des Proposals.')
    } finally {
      setProposalLoading(false)
    }
  }

  // 4) Apply via RPC
  async function applyProposalRPC() {
    setProposalError(null)
    setProposalInfo(null)

    try {
      await ensureAuthOrOpenModal()

      if (!canApplyNow) {
        setProposalInfo('Apply ist nur für editor/admin möglich und nur bei Status "submitted".')
        return
      }

      setProposalLoading(true)

      const { error } = await supabase.rpc('apply_proposal', { p_proposal_id: proposalId })
      if (error) throw error

      setProposalStatus('applied')
      setProposalInfo('Proposal angewendet. Daten werden neu geladen …')

      await reloadLocationFromDB()

      try {
        localStorage.removeItem(draftKey)
      } catch (_) {}

      setProposalInfo('Proposal angewendet und Location neu geladen.')
    } catch (e) {
      setProposalError(e?.message || 'Fehler beim Apply (RPC).')
    } finally {
      setProposalLoading(false)
    }
  }

  // ----------------------------
  // KI Übersetzen: Locations.description_*
  // ----------------------------
  const [translateOverwrite, setTranslateOverwrite] = useState(false)
  const [translateLoading, setTranslateLoading] = useState(false)
  const [translateError, setTranslateError] = useState(null)
  const [translateInfo, setTranslateInfo] = useState(null)

  const TARGETS = useMemo(() => ['en', 'fr', 'it', 'hr'], [])

  const canWriteTarget = (langCode) => {
    const k = `description_${langCode}`
    const cur = (form?.[k] ?? '') + ''
    if (translateOverwrite) return true
    return cur.trim() === ''
  }

  async function translateDescriptions() {
    setTranslateError(null)
    setTranslateInfo(null)

    const src = (form?.description_de ?? '').toString().trim()
    if (!src) {
      setTranslateError('Bitte zuerst eine deutsche Beschreibung eingeben.')
      return
    }

    const writableTargets = TARGETS.filter(canWriteTarget)
    if (writableTargets.length === 0) {
      setTranslateInfo('Alle Zielsprachen sind bereits befüllt. Aktiviere „Überschreiben“, wenn du neu übersetzen willst.')
      return
    }

    try {
      setTranslateLoading(true)

      const payload = {
        source_lang: 'de',
        targets: TARGETS,
        text: src,
        context: {
          type: 'location_description',
          location_id: value?.id ?? null,
          name_de: form?.name_de ?? null,
          display_name: form?.display_name ?? null,
        },
      }

      const res = await fetch('/api/c/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const t = await res.text().catch(() => '')
        throw new Error(t || `HTTP ${res.status}`)
      }

      const j = await res.json().catch(() => ({}))
      const dict = j?.translations && typeof j.translations === 'object' ? j.translations : j

      const updates = {}
      let applied = 0
      for (const lc of TARGETS) {
        const out = (dict?.[lc] ?? '').toString()
        if (!out.trim()) continue

        const k = `description_${lc}`
        if (canWriteTarget(lc)) {
          updates[k] = out
          applied++
        }
      }

      if (applied === 0) {
        setTranslateInfo('Übersetzung erhalten, aber aufgrund der Überschreibregel wurde nichts geändert.')
      } else {
        setForm((s) => ({ ...s, ...updates }))
        setTranslateInfo(`Übersetzung eingefügt: ${applied} Feld(er) aktualisiert.`)
      }
    } catch (e) {
      setTranslateError(e?.message || 'Fehler bei der Übersetzung.')
    } finally {
      setTranslateLoading(false)
    }
  }

  // ----------------------------
  // Proposal Badge UI
  // ----------------------------
  const badge = useMemo(() => {
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 10px',
      borderRadius: 999,
      fontSize: 12,
      border: '1px solid #e5e7eb',
      background: '#fff',
    }

    const dot = (bg) => ({
      width: 8,
      height: 8,
      borderRadius: 99,
      background: bg,
      display: 'inline-block',
    })

        let labelText = 'Kein Proposal'
    let dcol = '#94a3b8'

    if (hasChanges) {
      labelText = 'Änderungen erkannt'
      dcol = '#f59e0b'
    } else if (proposalStatus === 'draft') {
      labelText = 'Proposal: Draft'
      dcol = '#f59e0b'
    } else if (proposalStatus === 'submitted') {
      labelText = 'Proposal: Submitted'
      dcol = '#0ea5e9'
    } else if (proposalStatus === 'applied') {
      labelText = 'Proposal: Applied'
      dcol = '#16a34a'
    }

    return (
      <span style={base} title={proposalId ? `proposal_id: ${proposalId}` : ''}>
        <span style={dot(dcol)} />
        <strong style={{ fontWeight: 700 }}>{labelText}</strong>
        {hasChanges ? (
          <span style={{ opacity: 0.7 }}>· {proposalSummaryItems.length} Änderung(en) offen</span>
        ) : (
          <span style={{ opacity: 0.7 }}>· keine offenen Änderungen</span>
        )}
      </span>
    )
  }, [proposalId, proposalStatus, hasChanges, proposalSummaryItems.length])

const ProposalSummary = () => {
  if (!hasChanges) return null

  return (
    <div
      style={{
        border: '1px solid #bae6fd',
        background: '#f0f9ff',
        borderRadius: 12,
        padding: 12,
        margin: '12px 0',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>
        {t.summaryTitle} · {proposalSummaryItems.length} Änderung(en)
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {proposalSummaryItems.map((it) => (
          <div
            key={it.field_path}
            style={{
              border: '1px solid #e0f2fe',
              background: '#fff',
              borderRadius: 10,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{it.label}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {t.old}: {it.oldPreview}
            </div>
            <div style={{ fontSize: 13, color: '#0f172a' }}>
              {t.next}: {it.newPreview}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

  // ----------------------------
  // Auth Modal UI (sauber als Komponente, kein Top-Level-JSX)
  // ----------------------------
  const AuthModal = () => {
    if (!authModalOpen) return null
    if (typeof document === 'undefined') return null

    const overlayStyle = {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 16,
    }

    const dialogStyle = {
      width: 'min(560px, 96vw)',
      background: '#fff',
      borderRadius: 14,
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 30px rgba(0,0,0,0.20)',
      padding: 16,
    }

    return createPortal(
      <div
        onClick={() => setAuthModalOpen(false)}
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={dialogStyle}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Bei Wind2Horizon anmelden</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
  Melde dich an, um deine Rolle zu laden und verfügbare Bearbeitungsrechte zu aktivieren.
            </div>
            </div>
            <button
              type="button"
              onClick={() => setAuthModalOpen(false)}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                background: '#111827',
                color: '#fff',
              }}
            >
              Schließen
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ ...label, fontSize: 13 }}>
              Email
              <input
                style={{ ...input }}
                type="email"
                value={authEmail}
                onChange={(e) => {
                  e.stopPropagation()
                  setAuthEmail(e.target.value)
                }}
                onKeyDown={(e) => {
                  // ✅ verhindert, dass Map/Hotkeys/Form die Keys abgreifen
                  e.stopPropagation()
                  if (e.key === 'Enter') e.preventDefault()
                  if (e.key === 'Escape') setAuthModalOpen(false)
                }}
                onKeyUp={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.stopPropagation()}
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                disabled={authLoading}
                autoFocus
              />
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                type="button"
                onClick={sendLoginLink}
                disabled={authLoading}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: '1px solid #cbd5e1',
                  background: '#0ea5e9',
                  color: '#fff',
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {authLoading ? 'Sende…' : 'Login-Link senden'}
              </button>

              <button
                type="button"
                onClick={async () => {
                  setAuthError(null)
                  const { data } = await supabase.auth.getUser()
                  if (!data?.user?.id) {
                    setAuthError('Du bist noch nicht eingeloggt. Bitte Magic-Link verwenden.')
                    return
                  }
                  setAuthModalOpen(false)
                }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: '1px solid #cbd5e1',
                  background: '#111827',
                  color: '#fff',
                }}
              >
                Ich bin schon eingeloggt
              </button>
            </div>

            {authStep === 'sent' ? (
              <div style={{ marginTop: 10, fontSize: 12, color: '#0f766e' }}>
                Login-Link wurde gesendet. Nach dem Klick kommst du automatisch zurück zu diesem Marker.
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
                Du bekommst einen Magic-Link per Email. Danach kommst du automatisch wieder auf diese Seite zurück.
              </div>
            )}

            {authError ? (
              <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626' }}>
                <strong>Error:</strong> {authError}
              </div>
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    )
  }


  // ----------------------------
  // Render
  // ----------------------------
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSave && onSave(form)
      }}
      style={{ display: 'grid', gap: 16 }}
    >
      <AuthModal />

    {/* Login / Rolle / Proposal Status */}
<div
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    padding: 12,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#f8fafc',
  }}
>
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    {badge}

    {authUserId ? (
      <span style={{ fontSize: 12, color: '#334155' }}>
        Eingeloggt · Rolle: <strong>{userRole || 'user'}</strong>
      </span>
    ) : (
      <span style={{ fontSize: 12, color: '#64748b' }}>
        Nicht eingeloggt · Bearbeitung nur eingeschränkt möglich
      </span>
    )}
  </div>

  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    {!authUserId ? (
      <button
        type="button"
        onClick={() => {
          setAuthError(null)
          setAuthStep('email')
          setAuthModalOpen(true)
        }}
        style={{
          padding: '8px 12px',
          borderRadius: 10,
          border: '1px solid #cbd5e1',
          background: '#0ea5e9',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Einloggen
      </button>
    ) : (
      <button
        type="button"
        onClick={signOut}
        style={{
          padding: '8px 12px',
          borderRadius: 10,
          border: '1px solid #cbd5e1',
          background: '#fff',
          cursor: 'pointer',
        }}
      >
        Logout
      </button>
    )}
  </div>
</div>

      {proposalError ? <div style={{ color: '#dc2626', fontSize: 13 }}>{proposalError}</div> : null}
      {proposalInfo ? <div style={{ color: '#0f766e', fontSize: 13 }}>{proposalInfo}</div> : null}

      {/* ── Allgemein */}
      <h3>Allgemein</h3>
      <div className="c-section" style={boxStyle}>
        <label style={label}>
          Anzeigename
          <input style={input} value={form.display_name || ''} onChange={(e) => set('display_name', e.target.value)} />
        </label>

        <label style={label}>
          Aktiv
          <input type="checkbox" checked={!!form.active} onChange={(e) => set('active', e.target.checked)} />
        </label>

        {sortedCategories.length > 0 ? (
          <label style={label}>
            Kategorie
            <select style={input} value={form.category_id ?? ''} onChange={(e) => set('category_id', e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {sortedCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name_de || c.name_en || `#${c.id}`}
                </option>
              ))}
            </select>
            <small style={{ opacity: 0.7 }}>Aktuell: {catLabel(form.category_id ?? null)}</small>
          </label>
        ) : (
          <label style={label}>
            Kategorie-ID
            <input style={input} type="number" value={form.category_id ?? ''} onChange={(e) => set('category_id', e.target.value ? Number(e.target.value) : null)} />
            <small style={{ opacity: 0.7 }}>Name: {catLabel(form.category_id ?? null)}</small>
          </label>
        )}
      </div>

      {/* ── Koordinaten */}
      <h3>Koordinaten</h3>
      <div className="c-section" style={boxStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={label}>
            Lat
            <input style={input} type="number" step="0.000001" value={form.lat ?? ''} onChange={(e) => set('lat', e.target.value ? Number(e.target.value) : null)} />
          </label>
          <label style={label}>
            Lng
            <input style={input} type="number" step="0.000001" value={form.lng ?? ''} onChange={(e) => set('lng', e.target.value ? Number(e.target.value) : null)} />
          </label>
        </div>
      </div>

{/* ── Kontakt */}
<h3>Kontakt</h3>
<div className="c-section" style={boxStyle}>

<PolicyField
  label="Adresse"
  {...buildPolicyFieldProps('address', addressPolicy)}
/>

<PolicyField
  label="Website"
  {...buildPolicyFieldProps('website', websitePolicy)}
  placeholder="https://example.com"
/>

<PolicyField
  label="Telefon"
  {...buildPolicyFieldProps('phone', phonePolicy)}
/>

</div>

      {/* ── Mehrsprachige Namen */}
      <h3>Mehrsprachige Namen</h3>
      <div className="c-section" style={boxStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={label}>
            Deutsch
            <input style={input} value={form.name_de || ''} onChange={(e) => set('name_de', e.target.value)} />
          </label>
          <label style={label}>
            Englisch
            <input style={input} value={form.name_en || ''} onChange={(e) => set('name_en', e.target.value)} />
          </label>
          <label style={label}>
            Italienisch
            <input style={input} value={form.name_it || ''} onChange={(e) => set('name_it', e.target.value)} />
          </label>
          <label style={label}>
            Französisch
            <input style={input} value={form.name_fr || ''} onChange={(e) => set('name_fr', e.target.value)} />
          </label>
          <label style={label}>
            Kroatisch
            <input style={input} value={form.name_hr || ''} onChange={(e) => set('name_hr', e.target.value)} />
          </label>
        </div>
      </div>

      {/* ── Mehrsprachige Beschreibungen */}
      <h3>Mehrsprachige Beschreibungen</h3>
      <div className="c-section" style={boxStyle}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={translateDescriptions}
              disabled={translateLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: 10,
                background: '#0ea5e9',
                color: '#fff',
                cursor: translateLoading ? 'not-allowed' : 'pointer',
              }}
              title="Übersetzt description_de nach EN/FR/IT/HR und füllt die Felder (editierbar)"
            >
              {translateLoading ? 'Übersetze…' : 'DE → EN/FR/IT/HR übersetzen'}
            </button>

            <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={translateOverwrite} onChange={(e) => setTranslateOverwrite(e.target.checked)} />
              Bestehende Übersetzungen überschreiben
            </label>
          </div>

          <div style={{ fontSize: 12, color: '#64748b' }}>
            Quelle: <strong>Deutsch</strong> · Ziel: EN/FR/IT/HR
          </div>
        </div>

        {translateError ? <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{translateError}</div> : null}
        {translateInfo ? <div style={{ color: '#0f766e', fontSize: 13, marginBottom: 10 }}>{translateInfo}</div> : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

<PolicyField
  label="Deutsch"
  {...buildPolicyFieldProps('description_de', descriptionDePolicy)}
  multiline
/>

          <label style={label}>
            Englisch
            <textarea style={{ ...input, minHeight: 96 }} value={form.description_en || ''} onChange={(e) => set('description_en', e.target.value)} />
          </label>
          <label style={label}>
            Italienisch
            <textarea style={{ ...input, minHeight: 96 }} value={form.description_it || ''} onChange={(e) => set('description_it', e.target.value)} />
          </label>
          <label style={label}>
            Französisch
            <textarea style={{ ...input, minHeight: 96 }} value={form.description_fr || ''} onChange={(e) => set('description_fr', e.target.value)} />
          </label>
          <label style={label}>
            Kroatisch
            <textarea style={{ ...input, minHeight: 96 }} value={form.description_hr || ''} onChange={(e) => set('description_hr', e.target.value)} />
          </label>
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          Hinweis: Übersetzung füllt Felder nur im Formular. Zum Persistieren bitte danach <strong>Submit Proposal</strong> oder <strong>Speichern</strong>.
        </div>
      </div>

      {/* ── Wind & Schwell */}
      <h3>Wind &amp; Schwell</h3>
      <div className="c-section" style={boxStyle}>
        <WindProfilePanel
  locationId={Number(value.id)}
  canEdit={isPrivileged}
  isLoggedIn={!!authUserId}
  proposalMode={!!authUserId && !isPrivileged}
  onProposalItemChange={(item) => {
    setExtraProposalItems((prev) => {
      const withoutWind = prev.filter(
        (x) => !(x.table_name === 'location_values' && x.field_path === 'value_json')
      )
      return item ? [...withoutWind, item] : withoutWind
    })
  }}
/>
      </div>



<ProposalSummary />
      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
 
        <button
          type="button"
          onClick={submitProposal}
          disabled={proposalLoading || !hasChanges}
          style={{
            padding: '10px 14px',
            border: '1px solid #cbd5e1',
            borderRadius: 10,
            background: !hasChanges ? '#e2e8f0' : '#111827',
            color: !hasChanges ? '#64748b' : '#fff',
            cursor: proposalLoading || !hasChanges ? 'not-allowed' : 'pointer',
          }}
          title={!hasChanges ? t.noChanges : t.submitTitle}
        >
          {proposalLoading ? t.working : t.submitProposal}
        </button>

        {isPrivileged ? (
          <button
            type="button"
            onClick={applyProposalRPC}
            disabled={!canApplyNow || proposalLoading}
            style={{
              padding: '10px 14px',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              background: canApplyNow ? '#10b981' : '#e2e8f0',
              color: canApplyNow ? '#fff' : '#64748b',
              cursor: !canApplyNow || proposalLoading ? 'not-allowed' : 'pointer',
            }}
            title={!proposalId ? 'Kein Proposal vorhanden' : proposalStatus !== 'submitted' ? 'Apply ist nur bei Status "submitted" möglich' : 'Proposal anwenden (RPC)'}
          >
            {proposalLoading ? t.working : t.applyProposal}
          </button>
        ) : null}
      </div>

      <div style={{ fontSize: 12, color: '#64748b' }}>
        {t.flowHint}
      </div>

      {/* ── Fotos (User-Uploads) */}
<h3>Fotos (User-Uploads)</h3>
<div className="c-section" style={boxStyle}>
  <PhotosPanel
    locationId={value.id}
    canEdit={isPrivileged}
  />
  <div style={{ marginTop: 12 }}>
    <PhotosAddMore
      locationId={Number(value.id)}
      mode={isPrivileged ? 'direct' : 'proposal'}
      userId={authUserId}
    />
  </div>
</div>

    </form>
  )
}
